import type { HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';

export function Card({ className, children, ...rest }: HTMLAttributes<HTMLElement> & { children: ReactNode }) {
  return (
    <section className={cn('rounded-xl border border-border bg-card', className)} {...rest}>
      {children}
    </section>
  );
}

export function CardHeader({ title, action, className, as: H = 'h2' }: { title: ReactNode; action?: ReactNode; className?: string; as?: 'h2' | 'h3' }) {
  return (
    <div className={cn('flex min-h-14 items-center justify-between gap-3 border-b border-divider px-4 py-3 sm:px-5', className)}>
      <H className="text-[17px] font-semibold text-ink">{title}</H>
      {action}
    </div>
  );
}

/** Mono uppercase eyebrow, e.g. "WEEK OF OCT 12 – 16" (S1, S4, S5). */
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('font-mono text-xs tracking-[0.06em] text-muted uppercase', className)}>{children}</p>;
}

const AVATAR_TONES = ['bg-avatar text-ink', 'bg-strong text-strong-ink'];

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function Avatar({ name, size = 36, staff, className }: { name: string; size?: number; staff?: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.36)) }}
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full font-semibold', AVATAR_TONES[staff ? 1 : 0], className)}
    >
      {initials(name)}
    </span>
  );
}

export function EmptyState({ icon: Icon, text, action, className }: { icon: LucideIcon; text: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-10 text-center', className)}>
      <Icon aria-hidden className="size-8 text-faint" strokeWidth={1.5} />
      <p className="max-w-xs text-[15px] text-muted">{text}</p>
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden className={cn('block animate-pulse rounded-md bg-neutral-soft motion-reduce:animate-none', className)} />;
}

/** Progress bar with an accessible name and value. */
export function ProgressBar({ value, label, done, className }: { value: number; label: string; done?: boolean; className?: string }) {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={v}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-neutral-soft', className)}
    >
      <div className={cn('h-full rounded-full transition-[width] duration-200', done || v === 100 ? 'bg-success' : 'bg-primary')} style={{ width: `${v}%` }} />
    </div>
  );
}

const order = new Map<string, number>();
/** Registers the visible businesses so the first four get distinct S4 legend colours (then by hash). */
export function setBusinessOrder(ids: string[]) {
  ids.forEach((id, i) => {
    if (!order.has(id)) order.set(id, i);
  });
}

/** Business identity dot: colour from the S4 legend palette. */
export function businessTone(id: string): string {
  const i = order.get(id);
  if (i !== undefined) return ['bg-biz-1', 'bg-biz-2', 'bg-biz-3', 'bg-biz-4'][i % 4]!;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ['bg-biz-1', 'bg-biz-2', 'bg-biz-3', 'bg-biz-4'][Math.abs(h) % 4]!;
}

export function BizDot({ id, className }: { id: string; className?: string }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full', businessTone(id), className)} />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-muted">
      <span aria-hidden className="size-4 animate-spin rounded-full border-2 border-border border-t-primary" />
      <span className="sr-only">{label}</span>
    </span>
  );
}
